<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Auditoría</h1>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
          <div class="audit-entity-filter">
            <input
              v-model.trim="entityType"
              class="audit-entity-input"
              type="text"
              placeholder="Entidad (ej. Contract)"
              @keyup.enter="applyEntityFilter"
            />
            <input
              v-model.number="entityId"
              class="audit-entity-input audit-entity-input--id"
              type="number"
              min="1"
              placeholder="ID"
              @keyup.enter="applyEntityFilter"
            />
            <wz-button variant="primary-soft" size="sm" @click="applyEntityFilter">
              <i class="ri-search-2-line wz-mr-1"></i> Ver por entidad
            </wz-button>
            <wz-button
              v-if="entityType || entityId"
              variant="outline"
              size="sm"
              @click="clearEntityFilter"
            >
              Limpiar
            </wz-button>
          </div>
        </template>

        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData()">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-tooltip v-if="$can('manage', 'auditoria_descarga_excel')" text="Descargar">
            <wz-button variant="outline" @click="downloadReport()">
              <i class="ri-download-2-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-search-bar
            placeholder="Buscar por usuario, descripción"
            @search="onSearch"
            @input="search = $event"
          />
        </template>

        <template #filters v-if="showFiltersList">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th>Acción</th>
            <th>Sección</th>
            <th>Entidad</th>
            <th>Fecha y hora</th>
            <th>Usuario</th>
            <th>Descripción</th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="o in list" :key="o.id">
            <td>
              <wz-badge :variant="actionVariant(o.action)" pill>
                {{ getAction(o.action).label }}
              </wz-badge>
            </td>
            <td>{{ getSection(o.section).label }}</td>
            <td>{{ entityLabel(o) }}</td>
            <td v-local-date:hour="o.createdAt"></td>
            <td>{{ trimEmail(o.actor) }}</td>
            <td style="white-space: normal; max-width: 320px;">{{ o.description }}</td>
            <td style="text-align: center;">
              <wz-dropdown
                :items="[{ key: 'view', label: 'Ver detalle' }]"
                placement="left"
                @click="openDetail(o)"
              />
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <wz-pagination
          v-if="list.length"
          :total="total"
          :current-page="page"
          :per-page="pageSize"
          @change="onPaginationChange"
        />
        <div class="table-no-results" v-else>
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
      </wz-table-footer>

    </wz-table-container>

    <!-- Modal de detalle -->
    <wz-modal v-model="detailModal.show" size="xl" title="Detalle de auditoría">

      <!-- Loading -->
      <div v-if="detailModal.loading" class="audit-modal-loading">
        <i class="ri-loader-4-line ri-spin"></i>
        <p>Cargando...</p>
      </div>

      <div v-else-if="detailModal.data">

        <!-- Metadatos en grid -->
        <div class="audit-meta-grid">
          <div class="audit-meta-item">
            <span class="audit-meta-label">Acción</span>
            <wz-badge :variant="actionVariant(detailModal.data.action)" pill>
              {{ getAction(detailModal.data.action).label }}
            </wz-badge>
          </div>
          <div class="audit-meta-item">
            <span class="audit-meta-label">Sección</span>
            <span class="audit-meta-value">{{ getSection(detailModal.data.section).label }}</span>
          </div>
          <div class="audit-meta-item">
            <span class="audit-meta-label">Usuario</span>
            <span class="audit-meta-value">{{ trimEmail(detailModal.data.actor) }}</span>
          </div>
          <div class="audit-meta-item">
            <span class="audit-meta-label">Fecha y hora</span>
            <span class="audit-meta-value" v-local-date:hour="detailModal.data.createdAt"></span>
          </div>
          <div class="audit-meta-item" v-if="detailModal.data.entityType">
            <span class="audit-meta-label">Entidad</span>
            <span class="audit-meta-value">{{ entityLabel(detailModal.data) }}</span>
          </div>
        </div>

        <!-- Descripción -->
        <div class="audit-description">
          <span class="audit-meta-label">Descripción</span>
          <p class="audit-description-text">{{ detailModal.data.description }}</p>
        </div>

        <!-- Tabla de cambios -->
        <div v-if="diffEntries.length">
          <div class="audit-section-title">
            <i class="ri-git-diff-line"></i>
            Cambios detallados
          </div>
          <wz-table>
            <thead>
              <tr>
                <th style="width: 28%">Campo</th>
                <th style="width: 36%">Antes</th>
                <th style="width: 36%">Después</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in diffEntries" :key="entry.field">
                <td class="font-weight-600">{{ entry.field }}</td>
                <td class="text-muted audit-diff-cell">{{ entry.oldValue !== null ? entry.oldValue : '—' }}</td>
                <td class="audit-diff-cell">{{ entry.newValue !== null ? entry.newValue : '—' }}</td>
              </tr>
            </tbody>
          </wz-table>
        </div>

        <!-- Diff sin formato -->
        <div v-else-if="detailModal.data.diff && !diffParseOk">
          <div class="audit-section-title"><i class="ri-code-line"></i> Diff</div>
          <pre class="audit-raw-diff">{{ detailModal.data.diff }}</pre>
        </div>

        <!-- Sin cambios -->
        <div v-else class="audit-no-changes">
          <i class="ri-information-line"></i>
          Esta acción no genera registro de cambios.
        </div>

      </div>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { FilterMixin } from "@wizepe/wizeui"
import { PaginationMixin } from "@wizepe/wizeui"
import { Util } from "@wizepe/wizeui"
import fileDownload from "js-file-download";

import { Constants as LocalConstants } from "@/core/utils";
import { AuditLogService } from "@/core/services";

const HIDDEN_FIELDS = new Set([
  'id', 'status', 'isDeleted',
  'createdAt', 'updatedAt', 'createdBy', 'updatedBy',
  'filePath', 'fileUrl',
  'logoPath', 'signaturePath', 'sealPath',
  'logoUrl', 'signatureUrl', 'sealUrl',
  'signedDocumentPath', 'attachmentPath', 'attachmentUrl',
  'password', 'secret', 'token',
  'publicUuid', 'keycloakUserId',
  'documentationComplete', 'missingDocuments', 'currentContracts', 'photoAsset',
  'channelId', 'expenseLineId', 'payrollTypeIds',
  'displayStatus', 'workSchedule', 'schedule',
  'contractId', 'employeeId', 'conceptId',
  'workDaysList', 'vacationPeriodId', 'groupId',
])

const FIELD_LABELS = {
  code:                         'Código',
  identifierType:               'Tipo de documento',
  identifier:                   'Número de documento',
  name:                         'Nombres',
  lastNamePaternal:             'Apellido paterno',
  lastNameMaternal:             'Apellido materno',
  fullName:                     'Nombre completo',
  nationality:                  'Nacionalidad',
  gender:                       'Género',
  birthDate:                    'Fecha de nacimiento',
  address:                      'Dirección',
  ubigeo:                       'Ubigeo',
  ubigeoDescription:            'Distrito',
  personalPhone:                'Teléfono personal',
  personalEmail:                'Correo personal',
  emergencyContact:             'Contacto de emergencia',
  emergencyContactRelationship: 'Parentesco contacto emergencia',
  bloodType:                    'Grupo sanguíneo',
  maritalStatus:                'Estado civil',
  birthUbigeo:                  'Ubigeo de nacimiento',
  hasDisability:                'Tiene discapacidad',
  disabilityType:               'Tipo de discapacidad',
  conadisCertificate:           'Certificado CONADIS',
  educationLevel:               'Nivel educativo',
  profession:                   'Profesión',
  fifthCategoryStatus:          'Estado 5ta categoría',
  pensionSystem:                'Sistema pensionario',
  pensionEntity:                'Entidad pensionaria',
  cusppCode:                    'Código CUSPP',
  afpCommissionType:            'Tipo de comisión AFP',
  bankIdentifierType:           'Tipo doc. titular bancario',
  bankIdentifier:               'Doc. titular bancario',
  bankHolder:                   'Titular de cuenta',
  accountType:                  'Tipo de cuenta',
  bankAccount:                  'Número de cuenta',
  bankInterAccount:             'CCI',
  bank:                         'Banco',
  bankAccountVerified:          'Cuenta verificada',
  bankAccountVerifiedBy:        'Verificado por',
  ctsBank:                      'Banco CTS',
  ctsAccount:                   'Cuenta CTS',
  ctsCurrency:                  'Moneda CTS',
  number:                       'Número de contrato',
  employee:                     'Colaborador',
  company:                      'Empresa',
  area:                         'Área',
  position:                     'Cargo',
  contractType:                 'Tipo de contrato',
  startDate:                    'Fecha de inicio',
  finishDate:                   'Fecha de fin',
  baseSalary:                   'Sueldo base',
  weeklyHours:                  'Horas semanales',
  probationEndDate:             'Fin de período de prueba',
  salaryCurrency:               'Moneda',
  directManager:                'Jefe directo',
  laborRegime:                  'Régimen laboral',
  hasFamilyAllowance:           'Asignación familiar',
  occupationalCategory:         'Categoría ocupacional',
  workerType:                   'Tipo de trabajador',
  workSite:                     'Sede de trabajo',
  specialSituation:             'Situación especial',
  corporateEmail:               'Correo corporativo',
  essaludCode:                  'Código EsSalud',
  hasEps:                       'Tiene EPS',
  epsEntity:                    'Entidad EPS',
  hasSctrPension:               'SCTR Pensión',
  hasSctrHealth:                'SCTR Salud',
  hasLifeInsurance:             'Seguro de vida ley',
  lifeInsurancePolicy:          'Póliza vida ley',
  sctrPensionPolicy:            'Póliza SCTR Pensión',
  sctrHealthPolicy:             'Póliza SCTR Salud',
  corporatePhone:               'Celular corporativo',
  terminationDate:              'Fecha de cese',
  terminationType:              'Tipo de cese',
  cessationReasonName:          'Motivo de cese',
  terminationNotes:             'Notas de cese',
  channelName:                  'Canal',
  expenseLineName:              'Línea de gasto',
  payrollTypeNames:             'Tipos de planilla',
  signedDocumentName:           'Documento firmado',
  signedDocumentDate:           'Fecha documento firmado',
  hasSignedDocument:            'Tiene documento firmado',
  workDays:                     'Días laborales',
  entryTime:                    'Hora de entrada',
  exitTime:                     'Hora de salida',
  lunchStart:                   'Inicio de almuerzo',
  lunchEnd:                     'Fin de almuerzo',
  toleranceMinutes:             'Tolerancia (min)',
  effectiveHours:               'Horas efectivas',
  employeeName:                 'Colaborador',
  companyName:                  'Empresa',
  areaName:                     'Área',
  endDate:                      'Fecha de fin',
  calendarDays:                 'Días calendario',
  observations:                 'Observaciones',
  approvedBy:                   'Aprobado por',
  approvedAt:                   'Fecha de aprobación',
  rejectionReason:              'Motivo de rechazo',
  cancelledBy:                  'Cancelado por',
  cancelledAt:                  'Fecha de cancelación',
  cancellationReason:           'Motivo de cancelación',
  leaveType:                    'Tipo de descanso',
  employerDays:                 'Días a cargo del empleador',
  essaludDays:                  'Días a cargo de EsSalud',
  cittNumber:                   'Número CITT',
  medicalInstitution:           'Institución médica',
  attachmentName:               'Documento adjunto',
  doctorName:                   'Médico',
  doctorSpecialty:              'Especialidad médica',
  caseStatus:                   'Estado del caso',
  processStatus:                'Estado del proceso',
  insuranceType:                'Tipo de seguro',
  dailyPayment:                 'Pago diario',
  amountPaid:                   'Monto pagado',
  amountCollected:              'Monto cobrado',
  personInCharge:               'Responsable',
  totalDays:                    'Total de días',
  subsidizedDays:               'Días subsidiados',
  employeeAge:                  'Edad del colaborador',
  employeeIdentifierType:       'Tipo doc. colaborador',
  employeeIdentifier:           'Doc. colaborador',
  periodYear:                   'Año',
  periodMonth:                  'Mes',
  amount:                       'Monto',
  currency:                     'Moneda',
  origin:                       'Origen',
  description:                  'Descripción',
  installmentNumber:            'Cuota N°',
  totalInstallments:            'Total cuotas',
  totalAmount:                  'Monto total',
  installmentLabel:             'Etiqueta cuota',
  voucherType:                  'Tipo de comprobante',
  voucherNumber:                'N° de comprobante',
  conceptCode:                  'Código concepto',
  conceptName:                  'Nombre concepto',
  conceptType:                  'Tipo concepto',
  conceptNature:                'Naturaleza concepto',
  recordDate:                   'Fecha',
  dayType:                      'Tipo de día',
  attendanceStatus:             'Estado asistencia',
  tardinessMinutes:             'Tardanza (min)',
  overtimeHours:                'Horas extra',
  hoursWorked:                  'Horas trabajadas',
  ruc:                          'RUC',
  legalRepresentative:          'Representante legal',
  representativeTitle:          'Cargo del representante',
  payslipFooterText:            'Pie de boleta',
  isRequired:                   'Es obligatorio',
  mandatoryContributionRate:    'Aporte obligatorio',
  insurancePremiumRate:         'Prima de seguro',
  flowCommissionRate:           'Comisión por flujo',
  mixedCommissionRate:          'Comisión mixta',
}

export default {
  mixins: [FilterMixin, PaginationMixin],
  data() {
    return {
      list: [],
      entityType: '', // filtro "ver por entidad": tipo lógico (Contract, Employee, ...) → request.entityType
      entityId:   null, // filtro "ver por entidad": id del registro → request.entityId
      detailModal: {
        show:    false,
        loading: false,
        title:   'Detalle de auditoría',
        data:    null,
      },
      filterOptions: [
        {
          label:   'Acción',
          key:     'action',
          type:    'SINGLE',
          options: Object.values(LocalConstants.AuditLogAction),
        },
        {
          label:   'Sección',
          key:     'section',
          type:    'SINGLE',
          options: Object.values(LocalConstants.AuditLogSection),
        },
        {
          label:   'Fecha',
          key:     'date',
          type:    'DATE',
          isRange: true,
        },
      ],
    };
  },
  computed: {
    parsedDiff() {
      if (!this.detailModal.data || !this.detailModal.data.diff) {
        return { ok: true, entries: [] };
      }
      try {
        const parsed = JSON.parse(this.detailModal.data.diff);
        const entries = Object.keys(parsed)
          .filter(field => !HIDDEN_FIELDS.has(field))
          .map(field => ({
            field:    FIELD_LABELS[field] || field,
            oldValue: parsed[field].oldValue,
            newValue: parsed[field].newValue,
          }));
        return { ok: true, entries };
      } catch (e) {
        return { ok: false, entries: [] };
      }
    },
    diffEntries() { return this.parsedDiff.entries; },
    diffParseOk()  { return this.parsedDiff.ok; },
  },
  methods: {
    actionVariant(key) {
      return this.getAction(key)?.color || 'primary-solid';
    },
    onPaginationChange({ page, perPage }) {
      this.page = page;
      this.pageSize = perPage;
      this.loadData();
    },
    // Aplana el filtro de rango DATE al shape que espera el backend (@RequestBody FilterAuditLogRequest):
    // campos planos ISO dateStart/dateEnd (filtran sobre createdAt). WzFilterMenu guarda el rango como
    // objeto { start, end } bajo la clave 'date'; ese objeto lo descarta Jackson
    // (@JsonIgnoreProperties(ignoreUnknown=true)), por lo que sin aplanar el filtro nunca llega al backend.
    buildFilterPayload() {
      const data = { search: this.search, ...this.filters };
      const range = this.filters.date;
      delete data.date;
      if (range && range.start) data.dateStart = range.start;
      if (range && range.end)   data.dateEnd   = range.end;
      if (this.entityType)      data.entityType = this.entityType;
      if (this.entityId)        data.entityId   = this.entityId;
      return data;
    },
    // Etiqueta de la columna "Entidad": "Contract #123", solo el tipo si no hay id, o "—" si no aplica.
    entityLabel(o) {
      if (!o || !o.entityType) return '—';
      return o.entityId != null ? `${o.entityType} #${o.entityId}` : o.entityType;
    },
    // Filtro "ver por entidad": consulta todos los logs de un registro concreto. Persiste en la URL
    // (?entityType=&entityId=) para que el deep-link desde el detalle de la entidad funcione al recargar.
    applyEntityFilter() {
      this.page = 1;
      const query = { ...this.$route.query, page: 1 };
      if (this.entityType) query.entityType = this.entityType; else delete query.entityType;
      if (this.entityId)   query.entityId   = this.entityId;   else delete query.entityId;
      this.$router.push({ name: this.$route.name, query }).catch(err => {
        if (err && err.name !== 'NavigationDuplicated') console.error(err);
      });
      this.loadData();
    },
    clearEntityFilter() {
      this.entityType = '';
      this.entityId   = null;
      this.applyEntityFilter();
    },
    async loadData() {
      try {
        await this.$store.dispatch("app/loading", true);
        const params = { page: this.page - 1, size: this.pageSize };
        const data   = this.buildFilterPayload();
        const response = await AuditLogService.list(data, params);
        this.list  = response.payload.content.map(item => ({ ...item }));
        this.total = response.payload.totalElements;
        this.pages = response.payload.totalPages;
      } catch (e) {
        console.error(e);
        Alert.error(e.errors ? e.errors.message : "Error al cargar la auditoría");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    getAction(key) {
      return Util.searchValue(key, LocalConstants.AuditLogAction) || { label: key, color: 'dark' };
    },
    getSection(key) {
      return Util.searchValue(key, LocalConstants.AuditLogSection) || { label: key };
    },
    async downloadReport() {
      try {
        await this.$store.dispatch("app/loading", true);
        const data = this.buildFilterPayload();
        const response = await AuditLogService.download(data);
        fileDownload(response.data, Util.getFileName(response));
      } catch (error) {
        console.error(error);
        Alert.error("Error al descargar el reporte");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    async openDetail(o) {
      this.detailModal.show    = true;
      this.detailModal.loading = true;
      this.detailModal.data    = null;
      try {
        const response = await AuditLogService.getDetail(o.id);
        this.detailModal.data = response.payload;
      } catch (e) {
        console.error(e);
        Alert.error("Error al cargar el detalle");
        this.detailModal.show = false;
      } finally {
        this.detailModal.loading = false;
      }
    },
    trimEmail(val) {
      return Util.trimEmail(val);
    },
  },
  async mounted() {
    // Deep-link "ver historial de auditoría" desde el detalle de una entidad (?entityType=&entityId=).
    if (this.$route.query.entityType) this.entityType = this.$route.query.entityType;
    if (this.$route.query.entityId)   this.entityId   = Number(this.$route.query.entityId) || null;
    await this.loadData(this.page, this.pageSize);
  },
};
</script>

<style lang="stylus" scoped>
/* Filtro "ver por entidad" */
.audit-entity-filter
  display inline-flex
  align-items center
  gap 8px
  margin-left 8px
  flex-wrap wrap

.audit-entity-input
  height 36px
  box-sizing border-box
  padding 0 10px
  font-size 13px
  border 1px solid var(--admin-border-color)
  border-radius 6px
  background var(--admin-bg-panel)
  color var(--admin-text-main)
  &:focus
    outline none
    border-color var(--admin-primary-color)

.audit-entity-input--id
  width 90px

/* Loading */
.audit-modal-loading
  text-align center
  padding 40px 0
  color var(--admin-text-muted)
  i
    font-size 2rem
    display block
    margin-bottom 8px
  p
    margin 0
    font-size 14px

/* Metadatos */
.audit-meta-grid
  display grid
  grid-template-columns repeat(4, 1fr)
  gap 16px
  padding 16px 0
  border-bottom 1px solid var(--admin-border-color)
  margin-bottom 16px

.audit-meta-item
  display flex
  flex-direction column
  gap 6px

.audit-meta-label
  font-size 11px
  font-weight 600
  text-transform uppercase
  letter-spacing 0.05em
  color var(--admin-text-muted)

.audit-meta-value
  font-size 14px
  font-weight 600
  color var(--admin-text-main)

/* Descripción */
.audit-description
  display flex
  flex-direction column
  gap 6px
  margin-bottom 20px

.audit-description-text
  font-size 14px
  color var(--admin-text-main)
  margin 0
  line-height 1.5

/* Section title */
.audit-section-title
  font-size 13px
  font-weight 600
  color var(--admin-text-main)
  margin-bottom 10px
  display flex
  align-items center
  gap 6px
  i
    color var(--admin-primary-color)
    font-size 15px

/* Diff cell */
.audit-diff-cell
  font-size 12px
  font-family monospace
  word-break break-all
  max-width 300px

/* Raw diff */
.audit-raw-diff
  background var(--admin-bg-hover)
  border 1px solid var(--admin-border-color)
  border-radius 6px
  padding 12px 16px
  font-size 12px
  font-family monospace
  overflow-x auto
  color var(--admin-text-main)
  margin 0

/* No changes */
.audit-no-changes
  display flex
  align-items center
  gap 8px
  color var(--admin-text-muted)
  font-size 13px
  padding 12px 0
  i
    font-size 16px
</style>
